import React, { useRef, useState, useEffect } from "react";
import { Dropdown } from "react-bootstrap";
import { Download } from "react-bootstrap-icons";
import { AuthService, trackActionLog } from "../../services";
import { downloadXlsxFromMatrix } from "../utils/xlsxDownload";

/** Columns may set: exportHidden, canExportColumn(), requiresPermission, requiresCustomerTab */
const isColumnExportAllowed = (col) => {
  if (!col || col.key == null) return false;
  if (col.exportHidden === true) return false;
  if (typeof col.canExportColumn === "function") {
    try {
      return !!col.canExportColumn();
    } catch (_e) {
      return false;
    }
  }
  if (col.requiresPermission) {
    try {
      return AuthService.hasPermission(col.requiresPermission);
    } catch (_e) {
      return false;
    }
  }
  if (col.requiresCustomerTab) {
    try {
      return AuthService.canViewCustomerTab(col.requiresCustomerTab);
    } catch (_e) {
      return false;
    }
  }
  return true;
};

const mergeColumnsForExport = (incoming = [], previous = []) => {
  const prevShowByKey = new Map(previous.map((c) => [c.key, c.show === true]));
  return (incoming || [])
    .filter((c) => c && c.key != null && isColumnExportAllowed(c))
    .map((col) => ({
      ...col,
      show: prevShowByKey.has(col.key) ? prevShowByKey.get(col.key) : true,
    }));
};

const visibleExportColumns = (cols = []) =>
  (cols || []).filter((col) => col && col.key != null && col.show === true);

const getExportCellValue = (row, col) => {
  if (!row || !col) return "";
  if (typeof col.accessor === "function") {
    try {
      const v = col.accessor(row);
      return v == null ? "" : v;
    } catch (_e) {
      return "";
    }
  }
  const v = row[col.key];
  if (v == null) return "";
  if (typeof v === "object") {
    try {
      return JSON.stringify(v);
    } catch (_e) {
      return String(v);
    }
  }
  return v;
};

const escapeCsvField = (raw) => {
  if (raw == null) return "";
  const s = typeof raw === "string" ? raw : String(raw);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const escapeHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const Export = ({
  columns,
  data,
  filename = "export",
  customActions = [],
  show,
  onToggle,
  /** When set, action log entries include the page (e.g. "Tap export button (pdf) on Customer List page"). */
  actionLogPageName = "",
  /** When true, hide column checkboxes and Generate CSV / Excel / PDF (custom actions only). */
  hideBuiltinExportFormats = false,
  /** Rendered after Generate PDF (when shown), before Cancel. */
  customActionsBelowPdf = [],
}) => {
  const [internalShow, setInternalShow] = useState(false);
  const [exportColumns, setExportColumns] = useState(() =>
    mergeColumnsForExport(columns, [])
  );
  const scrollContainerRef = useRef(null);
  const scrollTopRef = useRef(0);
  const columnsKeysSigRef = useRef("");

  useEffect(() => {
    const list = columns || [];
    const sig = list.map((c) => c?.key).join("\0");
    if (sig === columnsKeysSigRef.current) return;
    columnsKeysSigRef.current = sig;
    setExportColumns((prev) => mergeColumnsForExport(list, prev));
  }, [columns]);

  // Use external control if provided, otherwise use internal state
  const showExportDropdown = show !== undefined ? show : internalShow;
  const baseHandleToggle = onToggle || ((isOpen) => setInternalShow(isOpen));
  const handleToggle = (isOpen, meta) => {
    if (isOpen) logExportTap("menu");
    baseHandleToggle(isOpen, meta);
  };
  const logExportTap = (target = "export") => {
    const page = `${actionLogPageName ?? ""}`.trim();
    const pageSuffix = page ? ` on ${page} page` : "";
    const targetLabel = `${target ?? ""}`.trim() || "export";
    const summary =
      targetLabel === "menu"
        ? `Open export menu${pageSuffix}`
        : `Export ${targetLabel}${pageSuffix}`;
    trackActionLog({
      actionVerb: "Export",
      summary,
      entityType: "export",
      entityId: filename,
    }).catch(() => {});
  };

  const handleColumnToggle = (columnKey) => {
    scrollTopRef.current = scrollContainerRef.current?.scrollTop || 0;
    const updatedColumns = exportColumns.map(col =>
      col.key === columnKey ? { ...col, show: col.show !== true } : col
    );
    setExportColumns(updatedColumns);
    requestAnimationFrame(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollTopRef.current;
      }
    });
  };

  const handleCancel = () => {
    setExportColumns(mergeColumnsForExport(columns || [], []).map((c) => ({ ...c, show: true })));
    handleToggle(false);
  };

  const handleDeselectAll = () => {
    scrollTopRef.current = scrollContainerRef.current?.scrollTop || 0;
    const updatedColumns = exportColumns.map((col) => ({ ...col, show: false }));
    setExportColumns(updatedColumns);
    requestAnimationFrame(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollTopRef.current;
      }
    });
  };

  const handleSelectAll = () => {
    scrollTopRef.current = scrollContainerRef.current?.scrollTop || 0;
    const updatedColumns = exportColumns.map((col) => ({ ...col, show: true }));
    setExportColumns(updatedColumns);
    requestAnimationFrame(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = scrollTopRef.current;
      }
    });
  };

  const generateCSV = () => {
    logExportTap("csv");
    const cols = visibleExportColumns(exportColumns);
    if (cols.length === 0) {
      window.alert("Select at least one column to export.");
      return;
    }
    const headers = cols.map((col) => escapeCsvField(col.label ?? col.key)).join(",");
    const rows = (data || []).map((row) =>
      cols.map((col) => escapeCsvField(getExportCellValue(row, col))).join(",")
    ).join("\n");

    const csvContent = `${headers}\n${rows}`;
    downloadFile(csvContent, `${filename}.csv`, "text/csv");
  };

  const generateExcel = async () => {
    logExportTap("excel");
    const cols = visibleExportColumns(exportColumns);
    if (cols.length === 0) {
      window.alert("Select at least one column to export.");
      return;
    }
    const headers = cols.map((col) => col.label ?? col.key);
    const body = (data || []).map((row) => cols.map((col) => getExportCellValue(row, col)));
    try {
      await downloadXlsxFromMatrix([headers, ...body], filename, "Export");
    } catch (e) {
      console.error(e);
      window.alert("Could not create Excel file.");
    }
  };

  const generatePDF = () => {
    logExportTap("pdf");
    const visibleColumns = visibleExportColumns(exportColumns);
    if (visibleColumns.length === 0) {
      window.alert("Select at least one column to export.");
      return;
    }
    
    // Create a simple HTML table for PDF generation
    const tableHTML = `
      <html>
        <head>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <table>
            <thead>
              <tr>
                ${visibleColumns.map((col) => `<th>${escapeHtml(col.label ?? col.key)}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${(data || []).map(row => `
                <tr>
                  ${visibleColumns.map(col => `<td>${escapeHtml(getExportCellValue(row, col))}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;
    
    // Use browser's print functionality to generate PDF
    const printWindow = window.open('', '_blank');
    printWindow.document.write(tableHTML);
    printWindow.document.close();
    printWindow.print();
  };

  const downloadFile = (content, filename, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const customExportMenu = React.forwardRef(
    ({ children, style, className, 'aria-labelledby': labeledBy }, ref) => {
      return (
        <div
          ref={ref}
          style={style}
          className={className}
          aria-labelledby={labeledBy}
        >
          <h6>Export Options</h6>
          <div className="app-main-content-fields-section margin-sm dropdown-container">
            <div className="me-4">
            {customActions.length > 0 && (
                  <>
                    {customActions.map((action, index) => (
                      <button 
                        key={index}
                        type="button"
                        className="btn btn-primary btn-sm mb-2" 
                        style={{ width: '100%' }} 
                        disabled={action.disabled === true}
                        onClick={() => {
                          if (!action.skipActionLog) {
                            logExportTap(action.actionLogLabel || action.label || "custom");
                          }
                          action.onClick?.();
                        }}
                      >
                        {action.label}
                      </button>
                    ))}
                    {!hideBuiltinExportFormats && (
                      <hr style={{ margin: '8px 0', borderColor: '#ddd' }} />
                    )}
                  </>
                )}
              {!hideBuiltinExportFormats && (
              <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '15px' }} ref={scrollContainerRef}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h6 style={{ fontSize: '14px', marginBottom: 0 }}>Select Columns:</h6>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0"
                      onClick={handleSelectAll}
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0"
                      onClick={handleDeselectAll}
                    >
                      Deselect All
                    </button>
                  </div>
                </div>
                {exportColumns.map((column) => (
                  <div key={column.key} style={{ marginBottom: '8px' }}>
                    <input
                      type="checkbox"
                      id={`export-column-${column.key}`}
                      checked={column.show === true}
                      onChange={() => handleColumnToggle(column.key)}
                    />
                    <label htmlFor={`export-column-${column.key}`} style={{ marginLeft: '8px' }}>
                      {column.label}
                    </label>
                  </div>
                ))}
              </div>
              )}
            </div>
          </div>
          <div className="list row">
            <div className="col-md-12">
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  marginTop: hideBuiltinExportFormats && customActions.length > 0 ? 0 : '15px',
                }}
              >
                {!hideBuiltinExportFormats && (
                  <>
                    <button className="btn btn-primary btn-sm" style={{ width: '100%' }} onClick={generateCSV}>
                      Generate CSV
                    </button>
                    <button className="btn btn-primary btn-sm" style={{ width: '100%' }} onClick={generateExcel}>
                      Generate Excel
                    </button>
                    <button className="btn btn-primary btn-sm" style={{ width: '100%' }} onClick={generatePDF}>
                      Generate PDF
                    </button>
                  </>
                )}
                {(customActionsBelowPdf || []).map((action, index) => (
                  <button
                    key={`below-pdf-${index}`}
                    type="button"
                    className="btn btn-primary btn-sm"
                    style={{ width: "100%" }}
                    disabled={action.disabled === true}
                    onClick={() => {
                      if (!action.skipActionLog) {
                        logExportTap(action.actionLogLabel || action.label || "custom");
                      }
                      action.onClick?.();
                    }}
                  >
                    {action.label}
                  </button>
                ))}
                <button className="btn btn-default btn-sm" style={{ width: '100%', marginTop: '8px' }} onClick={handleCancel}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    },
  );

  return (
    <Dropdown
      key={'export-dropdown'}
      id="export-dropdown"
      className="me-2"
      show={showExportDropdown}
      onToggle={handleToggle}
      autoClose={false}
    > 
      <Dropdown.Toggle variant="primary">
        <Download size={16} className="me-2"></Download>Export
      </Dropdown.Toggle>
      <Dropdown.Menu as={customExportMenu}/> 
    </Dropdown>
  );
};

export default Export; 